import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { ProjectCreationSettingsService, ProjectCreationEligibility } from '../../services/projectCreationSettingsService';
import { MessagesManagementService } from '../../services/messagesManagementService';
import { supabase } from '../../services/supabaseClient';
import { adminService } from '../../services/adminService';
import './SideNavbar.css';

type NavigationSubItem = {
  key: string;
  to: string;
  icon: string;
  label: string;
};

type NavigationItem = {
  key: string;
  to?: string;
  icon: string;
  label: string;
  isExpandable?: boolean;
  subItems?: NavigationSubItem[];
  dataTour?: string;
  hasNotifications?: boolean;
};

type NavigationSection = {
  key: string;
  title: string;
  items: NavigationItem[];
};

interface SideNavbarProps {
  isCollapsed: boolean;
  onOverlayClick?: () => void;
  onToggle?: () => void;
}

const SideNavbar: React.FC<SideNavbarProps> = ({ isCollapsed, onOverlayClick, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(false);
  const [projectEligibility, setProjectEligibility] = useState<ProjectCreationEligibility | null>(null);
  const [eligibilityLoading, setEligibilityLoading] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set(['tasks', 'teams'])); // Tasks and Teams expanded by default
  const [activeMessageCount, setActiveMessageCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  // Track the last collapsed state to detect actual changes
  const prevCollapsedRef = useRef(isCollapsed);
  // Track when sidebar just expanded to delay overlay activation
  const [showOverlay, setShowOverlay] = useState(false);

  // Refs for debouncing message count updates
  const messageCountUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMessageUpdatesRef = useRef<Set<string>>(new Set());

  const toggleMenu = useCallback((menuKey: string) => {
    if (isCollapsed) return; // Don't allow expand/collapse when sidebar is collapsed

    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuKey)) {
        newSet.delete(menuKey);
      } else {
        newSet.add(menuKey);
      }
      return newSet;
    });
  }, [isCollapsed]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle overlay visibility with delay to prevent catching toggle click
  useEffect(() => {
    // Only react to actual state changes
    if (prevCollapsedRef.current !== isCollapsed) {
      prevCollapsedRef.current = isCollapsed;

      if (!isCollapsed && isMobile) {
        // Sidebar just expanded - delay showing overlay
        setShowOverlay(false);
        const timer = setTimeout(() => setShowOverlay(true), 350);
        return () => clearTimeout(timer);
      } else {
        setShowOverlay(false);
      }
    }
  }, [isCollapsed, isMobile]);

  const checkProjectCreationEligibility = React.useCallback(async () => {
    if (!user) return;

    try {
      setEligibilityLoading(true);
      const eligibility = await ProjectCreationSettingsService.checkEligibility(user.id);
      setProjectEligibility(eligibility);
    } catch (error) {
      console.error('Error checking project creation eligibility:', error);
      // Set a default state that allows creation if there's an error
      setProjectEligibility({ canCreate: true, reasons: {} });
    } finally {
      setEligibilityLoading(false);
    }
  }, [user]);

  const checkActiveMessages = React.useCallback(async () => {
    if (!user) return;

    try {
      const count = await MessagesManagementService.getActiveMessageCount(user.id);
      setActiveMessageCount(count);
    } catch (error) {
      console.error('Error checking active messages:', error);
      setActiveMessageCount(0);
    }
  }, [user]);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminStatus = await adminService.isUserAdmin(user.id);
          setIsAdmin(adminStatus);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [user]);

  // Check project creation eligibility
  useEffect(() => {
    if (user) {
      checkProjectCreationEligibility();
      checkActiveMessages();
    } else {
      setProjectEligibility(null);
      setActiveMessageCount(0);
    }
  }, [user, checkProjectCreationEligibility, checkActiveMessages]);

  /**
   * Debounced message count update to prevent multiple rapid API calls
   * This batches multiple message events that happen within 800ms to better handle rapid individual operations
   */
  const debouncedUpdateMessageCount = useCallback(() => {
    console.log(`[SideNavbar] debouncedUpdateMessageCount called. Pending updates: ${pendingMessageUpdatesRef.current.size}`);

    if (messageCountUpdateTimeoutRef.current) {
      console.log(`[SideNavbar] Clearing existing timeout`);
      clearTimeout(messageCountUpdateTimeoutRef.current);
    }

    messageCountUpdateTimeoutRef.current = setTimeout(async () => {
      if (user && pendingMessageUpdatesRef.current.size > 0) {
        try {
          console.log(`[SideNavbar] Executing debounced update. Updating message count after ${pendingMessageUpdatesRef.current.size} pending updates`);
          const count = await MessagesManagementService.getActiveMessageCount(user.id);
          console.log(`[SideNavbar] getActiveMessageCount returned: ${count}`);
          setActiveMessageCount(count);
          pendingMessageUpdatesRef.current.clear();
          console.log(`[SideNavbar] ✅ Message count updated to: ${count}`);
        } catch (error) {
          console.error('[SideNavbar] ❌ Error updating active message count:', error);
          setActiveMessageCount(0);
        }
      } else {
        console.log(`[SideNavbar] Skipping update - no user or no pending updates`);
      }
    }, 800); // Wait 800ms after the last event before updating (increased for rapid individual operations)

    console.log(`[SideNavbar] New timeout set for 800ms from now`);
  }, [user]);

  // Set up real-time updates for project creation settings and messages
  useEffect(() => {
    if (!user) return;

    // Subscribe to settings changes for real-time updates
    const channel = supabase
      .channel('project_creation_settings_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'project_creation_settings'
      }, () => {
        // Refresh eligibility when settings change
        checkProjectCreationEligibility();
      })
      .subscribe();

    // Also subscribe to user points changes
    const userChannel = supabase
      .channel('user_points_changes')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'users',
        filter: `id=eq.${user.id}`
      }, () => {
        // Refresh eligibility when user points change
        checkProjectCreationEligibility();
      })
      .subscribe();

    // Subscribe to message updates for real-time notification badge updates
    const messageUnsubscribe = MessagesManagementService.subscribeToMessageUpdates(user.id, (message) => {
      // Add to pending updates and trigger debounced update
      console.log(`[SideNavbar] Database subscription event for message:`, {
        messageId: message.id,
        messageType: message.message_type,
        messageSource: message.messageSource,
        isDismissed: message.isDismissed,
        recipientUserId: user.id
      });
      pendingMessageUpdatesRef.current.add(message.id);
      console.log(`[SideNavbar] Added message to pending updates. Total pending: ${pendingMessageUpdatesRef.current.size}`);
      debouncedUpdateMessageCount();
    });

    // Listen for message dismissal events (localStorage changes don't trigger database subscriptions)
    const handleMessageDismissed = (event: any) => {
      // Add message IDs to pending updates and trigger debounced update
      const { messageIds } = event.detail || {};
      console.log(`[SideNavbar] Custom dismissal event for ${messageIds?.length || 0} messages`);
      if (messageIds && Array.isArray(messageIds)) {
        messageIds.forEach(id => pendingMessageUpdatesRef.current.add(id));
      }
      debouncedUpdateMessageCount();
    };

    // Listen for message read events
    const handleMessageRead = (event: any) => {
      // Add message IDs to pending updates and trigger debounced update
      const { messageIds } = event.detail || {};
      console.log(`[SideNavbar] Message read event for ${messageIds?.length || 0} messages`);
      if (messageIds && Array.isArray(messageIds)) {
        messageIds.forEach(id => pendingMessageUpdatesRef.current.add(id));
      }
      debouncedUpdateMessageCount();
    };

    window.addEventListener('messageDismissed', handleMessageDismissed);
    window.addEventListener('messageRead', handleMessageRead);

    // Note: Supabase Realtime subscriptions handle updates automatically
    // Periodic refresh removed to reduce unnecessary API calls

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(userChannel);
      messageUnsubscribe();
      window.removeEventListener('messageDismissed', handleMessageDismissed);
      window.removeEventListener('messageRead', handleMessageRead);
    };
  }, [user, checkProjectCreationEligibility, checkActiveMessages, debouncedUpdateMessageCount]);

  /**
   * Clean up timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (messageCountUpdateTimeoutRef.current) {
        clearTimeout(messageCountUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Handle navigation item click - validate session and auto-collapse on mobile
  const handleNavItemClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Import session validation utilities
    const { checkTokenExpirationSync, validateAndNavigate } = await import('../../utils/sessionValidationUtil');

    // Quick sync check first - if token appears expired, validate before navigation
    const { isExpired, hasSession } = checkTokenExpirationSync();

    if (!hasSession || isExpired) {
      // Prevent default navigation until we validate
      e.preventDefault();

      console.log('🔐 Session check on navigation click - attempting validation...');

      // Try to validate/refresh session
      const isValid = await validateAndNavigate();

      if (isValid) {
        // Session was refreshed successfully, now navigate
        const href = e.currentTarget.getAttribute('href');
        if (href) {
          window.location.href = href;
        }
      }
      // If not valid, validateAndNavigate already redirected to login
      return;
    }

    // Session is valid, proceed with collapse on mobile
    if (isMobile && !isCollapsed && onOverlayClick) {
      onOverlayClick();
    }
  };

  // Generate tooltip text for project creation requirements
  const getProjectCreationTooltip = (): string => {
    if (eligibilityLoading) return t('sidebar.checkingEligibility');
    if (!projectEligibility) return t('navigation.createProject');
    if (projectEligibility.canCreate) return t('navigation.createProject');

    const reasons = [];
    if (projectEligibility.reasons.insufficientPoints) {
      const { required, current } = projectEligibility.reasons.insufficientPoints;
      reasons.push(t('sidebar.needPoints', { required, current }));
    }
    if (projectEligibility.reasons.linkedinRequired) {
      reasons.push(t('sidebar.linkedinRequired'));
    }

    return `${t('sidebar.requirementsNotMet')}: ${reasons.join(', ')}`;
  };

  const navigationSections: NavigationSection[] = [
    {
      key: 'main',
      title: t('sidebar.main'),
      items: [
        { key: 'home', to: '/', icon: 'bi-house-door', label: t('navigation.home') },
        {
          key: 'journey',
          to: '/journey-list',
          icon: 'bi-compass',
          label: t('navigation.journey'),
          isExpandable: true,
          subItems: [
            { key: 'journey-list', to: '/journey-list', icon: 'bi-list-stars', label: t('navigation.myJourneys') },
            { key: 'journey-setup', to: '/startup-journey/setup', icon: 'bi-play-circle', label: t('navigation.startJourney') },
            // { key: 'journey-ai-ideas', to: '/journey/ai-idea-generator', icon: 'bi-lightbulb', label: t('sidebar.aiIdeas') },
            // { key: 'journey-launch-assets', to: '/journey/launch-assets-generator', icon: 'bi-palette', label: t('sidebar.launchAssets') }
          ]
        },
        {
          key: 'tasks',
          to: '/tasks',
          icon: 'bi-list-task',
          label: t('navigation.tasks'),
          isExpandable: true,
          subItems: [
            { key: 'tasks-task-taker', to: '/tasks/task-taker', icon: 'bi-person-check', label: t('navigation.taskTaker') },
            { key: 'tasks-publisher', to: '/tasks/publisher', icon: 'bi-briefcase', label: t('navigation.publisher') }
          ]
        },
        {
          key: 'questionnaires',
          to: '/questionnaires',
          icon: 'bi-patch-question-fill',
          label: t('navigation.questionnaires'),
          isExpandable: true,
          subItems: [
            { key: 'questionnaires-list', to: '/questionnaires', icon: 'bi-list-ul', label: t('navigation.takeSurveys') },
            { key: 'questionnaires-publisher', to: '/questionnaires/publisher', icon: 'bi-ui-checks', label: t('navigation.mySurveys') }
          ]
        },
        {
          key: 'teams',
          to: '/teams',
          icon: 'bi-people-fill',
          label: t('navigation.teams'),
          isExpandable: true,
          subItems: [
            { key: 'teams-join', to: '/teams/join', icon: 'bi-search', label: t('navigation.joinTeam') },
            { key: 'teams-setup', to: '/teams/setup', icon: 'bi-gear-fill', label: t('navigation.setupTeam') }
          ]
        },
        { key: 'pre-release', to: '/pre-release', icon: 'bi-rocket-takeoff', label: t('navigation.preRelease') },
        { key: 'forums', to: '/forums', icon: 'bi-chat-dots', label: t('navigation.forums'), dataTour: 'forums-nav' },
        { key: 'messages', to: '/messages', icon: 'bi-envelope', label: t('navigation.messages'), hasNotifications: true }
      ]
    },
    {
      key: 'create',
      title: t('sidebar.create'),
      items: [
        { key: 'create-task', to: '/create-task', icon: 'bi-clipboard-plus', label: t('navigation.createTask') },
        { key: 'create-team-request', to: '/create-team-request', icon: 'bi-people-fill', label: t('navigation.createTeam') },
        { key: 'create-questionnaire', to: '/create-questionnaire', icon: 'bi-ui-checks', label: t('navigation.createQuestionnaire') },
        { key: 'create-pre-release-project', to: '/create-pre-release-project', icon: 'bi-rocket-takeoff', label: t('navigation.createProject') },
        { key: 'publisher-dashboard', to: '/publisher-dashboard', icon: 'bi-graph-up-arrow', label: t('navigation.publisherDashboard') }
      ]
    },
    {
      key: 'account',
      title: t('sidebar.account'),
      items: [
        { key: 'profile', to: '/profile', icon: 'bi-person', label: t('common.profile') },
        { key: 'points-history', to: '/points-history', icon: 'bi-star', label: t('navigation.pointsHistory') },
        { key: 'claim-bonuses', to: '/claim-bonuses', icon: 'bi-gift', label: t('navigation.claimBonuses') }
      ]
    },
    {
      key: 'support',
      title: t('sidebar.support'),
      items: [
        { key: 'help', to: '/help', icon: 'bi-question-circle', label: t('common.help') },
        { key: 'contact', to: '/contact', icon: 'bi-envelope', label: t('common.contact') }
      ]
    },
    // Admin section - only shown to admin users
    ...(isAdmin ? [{
      key: 'admin',
      title: t('sidebar.admin'),
      items: [
        { key: 'settings', to: '/settings', icon: 'bi-gear', label: t('navigation.adminSettings') },
        { key: 'moderation', to: '/admin/moderation', icon: 'bi-shield-check', label: t('sidebar.moderation') }
      ]
    }] : [])
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // const isTasksActive = () => {
  //   return location.pathname.startsWith('/tasks');
  // };
  // Track if we just handled a touch event to prevent double-firing
  const touchHandledRef = useRef(false);

  // Handle toggle click - only fires on click, touch is handled separately
  const handleToggleClick = useCallback((e: React.MouseEvent) => {
    // Skip if this was already handled by touch
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (onToggle) {
      onToggle();
    }
  }, [onToggle]);

  // Handle toggle touch - fires on touch devices
  const handleToggleTouch = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    if (onToggle) {
      onToggle();
    }
    // Reset after a short delay
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);
  }, [onToggle]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (onOverlayClick) {
      onOverlayClick();
    }
  }, [onOverlayClick]);

  // Handle overlay touch
  const handleOverlayTouch = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    if (onOverlayClick) {
      onOverlayClick();
    }
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);
  }, [onOverlayClick]);

  return (
    <>
      {/* Overlay for mobile - only show after delay to prevent catching toggle click */}
      {showOverlay && isMobile && !isCollapsed && (
        <div
          className="sidebar-overlay"
          onClick={handleOverlayClick}
          onTouchEnd={handleOverlayTouch}
        />
      )}

      <aside className={`side-navbar ${isCollapsed ? 'collapsed' : 'expanded'}`}>
        {/* Navigation Sections */}
        <nav className="sidebar-nav">
          {navigationSections.map((section) => (
            <div key={section.key} className="nav-section">
              {!isCollapsed && section.key === 'main' && (
                <div className="section-title-with-toggle">
                  <div className="section-title">{section.title}</div>
                  {onToggle && (
                    <button
                      className="sidebar-toggle-beside-main"
                      onClick={handleToggleClick}
                      onTouchEnd={handleToggleTouch}
                      title={t('sidebar.collapseSidebar')}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  )}
                </div>
              )}

              {!isCollapsed && section.key !== 'main' && (
                <div className="section-title">
                  {section.title}
                </div>
              )}

              {isCollapsed && (
                <div className="collapsed-toggle-section">
                  {onToggle && section.key === 'main' && (
                    <button
                      className="sidebar-toggle-collapsed"
                      onClick={handleToggleClick}
                      onTouchEnd={handleToggleTouch}
                      title={t('sidebar.expandSidebar')}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  )}
                </div>
              )}

              <ul className="nav-items">
                {section.items.map((item) => {
                  const isProjectCreation = item.to === '/create-pre-release-project';
                  const canCreateProject = projectEligibility?.canCreate ?? true;
                  const isEligibilityLoading = isProjectCreation && eligibilityLoading;
                  const hasSubItems = item.isExpandable && item.subItems;
                  const menuKey = item.key; // Use a stable key for the menu
                  const isExpanded = expandedMenus.has(menuKey);
                  const isTasksMenu = item.key === 'tasks';
                  const isQuestionnairesMenu = item.key === 'questionnaires';
                  const isTeamsMenu = item.key === 'teams';

                  // Check if any sub-item is currently active
                  const hasActiveSubItem = hasSubItems && item.subItems?.some(subItem => {
                    if (subItem.to === '/questionnaires') {
                      // Special case: exact match for /questionnaires path
                      return location.pathname === '/questionnaires';
                    }
                    // For other paths, use the standard isActive logic
                    return isActive(subItem.to);
                  });

                  // Parent menu should only be active if the main path matches exactly and no sub-item is active
                  const isParentMenuActive = () => {
                    if (isTasksMenu) {
                      // For tasks, only show parent as active if on exact /tasks path and no sub-item is active
                      return location.pathname === '/tasks' && !hasActiveSubItem;
                    }
                    if (isQuestionnairesMenu) {
                      // For questionnaires, never show parent as active since sub-items handle all cases
                      return false;
                    }
                    if (isTeamsMenu) {
                      // For teams, never show parent as active since sub-items handle all cases
                      return false;
                    }
                    return false;
                  };

                  return (
                    <li key={item.key}>
                      {hasSubItems && !isCollapsed ? (
                        // Expandable menu item
                        <div className="nav-item-wrapper">
                          <div
                            className={`nav-item expandable ${isParentMenuActive() ? 'active' : ''} ${(isTasksMenu || isQuestionnairesMenu || isTeamsMenu) ? 'nav-item-blue' : ''
                              }`}
                            onClick={() => toggleMenu(menuKey)}
                          >
                            <i className={`bi ${item.icon} nav-icon`}></i>
                            <span className="nav-label">{item.label}</span>
                            <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} expand-icon`}></i>
                            {isParentMenuActive() && <div className="active-indicator"></div>}
                          </div>

                          {/* Sub-items */}
                          {isExpanded && (
                            <ul className="nav-subitems">
                              {item.subItems?.map((subItem) => {
                                // More precise active detection for sub-items
                                const isSubItemActive = () => {
                                  if (subItem.to === '/questionnaires') {
                                    // For the base questionnaires path, only active if exactly on that path
                                    return location.pathname === '/questionnaires';
                                  }
                                  // For other paths, use the standard isActive logic
                                  return isActive(subItem.to);
                                };

                                return (
                                  <li key={subItem.key}>
                                    <Link
                                      to={subItem.to}
                                      className={`nav-subitem ${isSubItemActive() ? 'active' : ''}`}
                                      onClick={handleNavItemClick}
                                    >
                                      <i className={`bi ${subItem.icon} nav-icon`}></i>
                                      <span className="nav-label">{subItem.label}</span>
                                      {isSubItemActive() && <div className="active-indicator"></div>}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      ) : (
                        // Regular menu item
                        item.to ? (
                          <Link
                            to={
                              isTasksMenu && isCollapsed ? '/tasks/task-taker' :
                                isQuestionnairesMenu && isCollapsed ? '/questionnaires' :
                                  isTeamsMenu && isCollapsed ? '/teams/join' :
                                    item.to
                            }
                            className={`nav-item ${isTasksMenu ? (isParentMenuActive() ? 'active' : '') :
                              isQuestionnairesMenu ? (isParentMenuActive() ? 'active' : '') :
                                isTeamsMenu ? (isParentMenuActive() ? 'active' : '') :
                                  (isActive(item.to) ? 'active' : '')
                              } ${isProjectCreation && !canCreateProject ? 'nav-item-locked' : ''
                              } ${isEligibilityLoading ? 'nav-item-loading' : ''} ${(isTasksMenu || isQuestionnairesMenu || isTeamsMenu) ? 'nav-item-blue' : ''
                              }`}
                            title={isCollapsed ? (isProjectCreation ? getProjectCreationTooltip() : item.label) : ''}
                            onClick={handleNavItemClick}
                            data-tour={(item as any).dataTour}
                          >
                            <i className={`bi ${item.icon} nav-icon`}></i>
                            {!isCollapsed && (
                              <span className="nav-label">
                                {item.label}
                                {isProjectCreation && !canCreateProject && (
                                  <i className="bi bi-lock-fill nav-lock-icon" title={getProjectCreationTooltip()}></i>
                                )}
                                {isEligibilityLoading && (
                                  <i className="bi bi-arrow-clockwise nav-loading-icon"></i>
                                )}
                              </span>
                            )}
                            {/* Notification badge for messages */}
                            {item.hasNotifications && activeMessageCount > 0 && (
                              <span className="notification-badge" title={t('sidebar.activeMessages', { count: activeMessageCount })}>
                                {activeMessageCount > 99 ? '99+' : activeMessageCount}
                              </span>
                            )}
                            {(
                              (isTasksMenu && isParentMenuActive()) ||
                              (isQuestionnairesMenu && isParentMenuActive()) ||
                              (isTeamsMenu && isParentMenuActive()) ||
                              (!isTasksMenu && !isQuestionnairesMenu && !isTeamsMenu && isActive(item.to))
                            ) && <div className="active-indicator"></div>}
                            {isProjectCreation && !isCollapsed && !canCreateProject && (
                              <div className="eligibility-tooltip" title={getProjectCreationTooltip()}>
                                <i className="bi bi-info-circle"></i>
                              </div>
                            )}
                          </Link>
                        ) : null
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        {!isCollapsed && (
          <div className="sidebar-footer">
            <div className="footer-text">
              <small>{t('common.copyright')}</small>
            </div>
          </div>
        )}
      </aside>
    </>
  );
};

export default SideNavbar;
